import {
  CASE_CATEGORIES,
  CASE_CHANNELS,
  CASE_SEVERITIES,
  CASE_STATUSES,
  CASE_TRANSITIONS,
  FOLLOW_UP_OUTCOMES,
  REMINDER_RULE_CODES,
  type CaseActivityType,
  type CaseCategory,
  type CaseChannel,
  type CaseSeverity,
  type CaseStatus,
  type FollowUpOutcome,
  type FollowUpStatus,
  type FollowUpType,
  type ReminderRuleCode,
  type ReminderStatus,
} from '@nks/shared';
import { api } from '../../lib/api-client';

export { CASE_CATEGORIES, CASE_CHANNELS, CASE_SEVERITIES, CASE_STATUSES, FOLLOW_UP_OUTCOMES, REMINDER_RULE_CODES };
export type {
  CaseActivityType,
  CaseCategory,
  CaseChannel,
  CaseSeverity,
  CaseStatus,
  FollowUpOutcome,
  FollowUpStatus,
  FollowUpType,
  ReminderRuleCode,
  ReminderStatus,
};

/** Moves offered by Change status: Resolved has its own drawer because it needs a resolution. */
export function statusMoves(from: CaseStatus): CaseStatus[] {
  return CASE_TRANSITIONS[from].filter((s) => s !== 'RESOLVED');
}
export const canResolve = (from: CaseStatus): boolean => CASE_TRANSITIONS[from].includes('RESOLVED');
export const isOpenCase = (from: CaseStatus): boolean => from !== 'RESOLVED' && from !== 'CLOSED';

export interface CaseActivity {
  uid: string;
  type: string;
  note: string;
  createdAt: string;
  createdBy: string | null;
}

export interface CaseHistoryEntry {
  uid: string;
  fromStatus: string | null;
  toStatus: string;
  reason: string | null;
  changedBy: string | null;
  at: string;
}

export interface ServiceCase {
  uid: string;
  caseNo: string;
  status: CaseStatus;
  channel: string;
  category: string;
  severity: string;
  subject: string;
  description: string | null;
  customer: { uid: string; name: string; phone: string };
  unit: { uid: string; chassisNo: string; plateNo: string | null } | null;
  link: { type: string; uid: string; ref: string } | null;
  owner: { uid: string; name: string };
  slaResponseDueAt: string;
  slaResolveDueAt: string;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  resolution: string | null;
  satisfactionScore: number | null;
  breached: { response: boolean; resolve: boolean };
  createdAt: string;
}

export interface ServiceCaseDetail extends ServiceCase {
  activities: CaseActivity[];
  history: CaseHistoryEntry[];
}

export interface CasePage {
  items: ServiceCase[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface ListCasesParams {
  q?: string;
  status?: CaseStatus;
  owner?: string;
  overdue?: boolean;
  cursor?: string;
}

function casesQuery(params: ListCasesParams, extra: Record<string, string> = {}): string {
  const search = new URLSearchParams(extra);
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.owner !== undefined) search.set('owner', params.owner);
  if (params.overdue === true) search.set('overdue', '1');
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export const listCases = (params: ListCasesParams): Promise<CasePage> =>
  api<CasePage>(`/service-desk/cases${casesQuery(params)}`);

export const exportCasesCsv = (params: ListCasesParams): Promise<string> =>
  api<string>(`/service-desk/cases${casesQuery(params, { format: 'csv' })}`);

export const getCase = (uid: string): Promise<ServiceCaseDetail> =>
  api<ServiceCaseDetail>(`/service-desk/cases/${uid}`);

export interface CreateCaseInput {
  customerUid: string;
  channel: CaseChannel;
  category: CaseCategory;
  severity: CaseSeverity;
  subject: string;
  description?: string;
  unitUid?: string;
  linkType?: LinkableType;
  linkUid?: string;
}

export const createCase = (input: CreateCaseInput): Promise<ServiceCaseDetail> =>
  api<ServiceCaseDetail>('/service-desk/cases', { method: 'POST', body: input });

export const addCaseActivity = (
  uid: string,
  input: { type: CaseActivityType; note: string },
): Promise<ServiceCaseDetail & { waLink?: string }> =>
  api(`/service-desk/cases/${uid}/activities`, { method: 'POST', body: input });

export const transitionCase = (
  uid: string,
  input: { to: CaseStatus; reason?: string; resolution?: string; satisfactionScore?: number },
): Promise<ServiceCaseDetail> => api(`/service-desk/cases/${uid}/transitions`, { method: 'POST', body: input });

export const assignCase = (uid: string, input: { ownerUid: string; reason?: string }): Promise<ServiceCaseDetail> =>
  api(`/service-desk/cases/${uid}/assign`, { method: 'POST', body: input });

// ── Follow-ups ────────────────────────────────────────────────────────

export interface FollowUp {
  uid: string;
  type: FollowUpType;
  status: FollowUpStatus;
  customer: { uid: string; name: string; phone: string };
  unit: { uid: string; chassisNo: string; plateNo: string | null } | null;
  source: { type: string; uid: string; ref: string } | null;
  owner: { uid: string; name: string };
  dueAt: string;
  overdue: boolean;
  attempts: number;
  outcome: FollowUpOutcome | null;
  note: string | null;
  satisfactionScore: number | null;
  completedAt: string | null;
  case: { uid: string; caseNo: string } | null;
  createdAt: string;
}

export interface Paged<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

export type FollowUpView = 'mine' | 'today' | 'overdue' | 'everyone';

export interface ListFollowUpsParams {
  view: FollowUpView;
  cursor?: string;
}

function followUpsQuery(params: ListFollowUpsParams, extra: Record<string, string> = {}): string {
  const search = new URLSearchParams(extra);
  search.set('status', 'OPEN');
  search.set('mine', params.view === 'everyone' ? '0' : '1');
  if (params.view === 'today') search.set('due', 'today');
  if (params.view === 'overdue') search.set('due', 'overdue');
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  return `?${search.toString()}`;
}

export const listFollowUps = (params: ListFollowUpsParams): Promise<Paged<FollowUp>> =>
  api<Paged<FollowUp>>(`/service-desk/follow-ups${followUpsQuery(params)}`);

export const exportFollowUpsCsv = (params: ListFollowUpsParams): Promise<string> =>
  api<string>(`/service-desk/follow-ups${followUpsQuery(params, { format: 'csv' })}`);

export interface OpenCaseInput {
  category: CaseCategory;
  severity: CaseSeverity;
  subject: string;
  description: string;
}

export interface CompleteFollowUpInput {
  outcome: FollowUpOutcome;
  note?: string;
  satisfactionScore?: number;
  openCase?: OpenCaseInput;
}

export const completeFollowUp = (uid: string, input: CompleteFollowUpInput): Promise<FollowUp> =>
  api<FollowUp>(`/service-desk/follow-ups/${uid}/complete`, { method: 'POST', body: input });

export const retryFollowUp = (uid: string, input: { dueAt: string }): Promise<FollowUp> =>
  api<FollowUp>(`/service-desk/follow-ups/${uid}/retry`, { method: 'POST', body: input });

export interface CreateFollowUpInput {
  type: 'REACTIVATION' | 'UPGRADE';
  customerUid: string;
  unitUid?: string;
}

export const createFollowUp = (input: CreateFollowUpInput): Promise<FollowUp> =>
  api<FollowUp>('/service-desk/follow-ups', { method: 'POST', body: input });

// ── Reminders ─────────────────────────────────────────────────────────

export interface Reminder {
  uid: string;
  status: ReminderStatus;
  ruleCode: ReminderRuleCode;
  /** Kuala Lumpur calendar date, YYYY-MM-DD. */
  dueDate: string;
  dueMileage: number | null;
  customer: { uid: string; name: string; phone: string };
  unit: { uid: string; chassisNo: string; plateNo: string | null };
  sentAt: string | null;
  dismissReason: string | null;
  createdAt: string;
}

export interface ListRemindersParams {
  status?: ReminderStatus;
  dueWithinDays?: number;
  cursor?: string;
}

function remindersQuery(params: ListRemindersParams, extra: Record<string, string> = {}): string {
  const search = new URLSearchParams(extra);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.dueWithinDays !== undefined) search.set('dueWithinDays', String(params.dueWithinDays));
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export const listReminders = (params: ListRemindersParams): Promise<Paged<Reminder>> =>
  api<Paged<Reminder>>(`/service-desk/reminders${remindersQuery(params)}`);

export const exportRemindersCsv = (params: ListRemindersParams): Promise<string> =>
  api<string>(`/service-desk/reminders${remindersQuery(params, { format: 'csv' })}`);

export const sendReminder = (uid: string): Promise<{ waLink: string }> =>
  api<{ waLink: string }>(`/service-desk/reminders/${uid}/send`, { method: 'POST' });

export const dismissReminder = (uid: string, input: { reason: string }): Promise<Reminder> =>
  api<Reminder>(`/service-desk/reminders/${uid}/dismiss`, { method: 'POST', body: input });

// ── Retention ─────────────────────────────────────────────────────────

export type RetentionList = 'no-service' | 'upgrade';

export interface RetentionRow {
  customerUid: string;
  customerName: string;
  phone: string;
  unitUid: string;
  model: string;
  plateNo: string | null;
  lastServiceAt?: string;
  deliveredAt?: string;
  hasOpenFollowUp: boolean;
  marketingConsent: boolean;
}

function retentionQuery(cursor: string | undefined, extra: Record<string, string> = {}): string {
  const search = new URLSearchParams(extra);
  if (cursor !== undefined && cursor.length > 0) search.set('cursor', cursor);
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export const listRetention = (list: RetentionList, cursor?: string): Promise<Paged<RetentionRow>> =>
  api<Paged<RetentionRow>>(`/service-desk/retention/${list}${retentionQuery(cursor)}`);

export const exportRetentionCsv = (list: RetentionList): Promise<string> =>
  api<string>(`/service-desk/retention/${list}${retentionQuery(undefined, { format: 'csv' })}`);

// ── Settings ──────────────────────────────────────────────────────────

export interface SlaTarget {
  category: CaseCategory;
  severity: CaseSeverity;
  responseMinutes: number;
  resolveMinutes: number;
}

export interface ReminderRule {
  code: ReminderRuleCode;
  months: number;
  kilometres: number;
  isActive: boolean;
}

export const getSlaTargets = (): Promise<{ targets: SlaTarget[] }> =>
  api<{ targets: SlaTarget[] }>('/service-desk/settings/sla-targets');

export const saveSlaTargets = (targets: SlaTarget[]): Promise<{ targets: SlaTarget[] }> =>
  api<{ targets: SlaTarget[] }>('/service-desk/settings/sla-targets', { method: 'PUT', body: { targets } });

export const getReminderRules = (): Promise<{ rules: ReminderRule[] }> =>
  api<{ rules: ReminderRule[] }>('/service-desk/settings/reminder-rules');

export const saveReminderRules = (rules: ReminderRule[]): Promise<{ rules: ReminderRule[] }> =>
  api<{ rules: ReminderRule[] }>('/service-desk/settings/reminder-rules', { method: 'PUT', body: { rules } });

// ── Pickers. Plain HTTP reads of other modules' lists: this feature never
// imports another feature's code (CLAUDE.md §4).

interface Page<T> {
  items: T[];
}

export interface CustomerOption {
  uid: string;
  name: string;
  phone: string;
}

export const searchCustomers = async (q: string): Promise<CustomerOption[]> =>
  (await api<Page<CustomerOption>>(`/customers?${new URLSearchParams({ q }).toString()}`)).items;

export interface UnitOption {
  uid: string;
  chassisNo: string;
  plateNo: string | null;
  model: string | null;
}

export const fetchCustomerUnits = async (customerUid: string): Promise<UnitOption[]> =>
  (await api<Page<UnitOption>>(`/units?${new URLSearchParams({ customerUid }).toString()}`)).items;

export const LINKABLE_TYPES = ['SALE', 'JOB_CARD', 'DELIVERY'] as const;
export type LinkableType = (typeof LINKABLE_TYPES)[number];

export interface LinkOption {
  uid: string;
  label: string;
}

/** The customer's own bookings, job cards or deliveries, for the case's optional link. */
export async function fetchCustomerLinks(customerUid: string, type: LinkableType): Promise<LinkOption[]> {
  const qs = new URLSearchParams({ customerUid }).toString();
  switch (type) {
    case 'SALE':
      return (await api<Page<{ uid: string; bookingNo: string }>>(`/bookings?${qs}`)).items.map((r) => ({
        uid: r.uid,
        label: r.bookingNo,
      }));
    case 'JOB_CARD':
      return (await api<Page<{ uid: string; jobNo: string }>>(`/workshop/job-cards?${qs}`)).items.map((r) => ({
        uid: r.uid,
        label: r.jobNo,
      }));
    case 'DELIVERY':
      return (await api<Page<{ uid: string; doNo: string }>>(`/deliveries?${qs}`)).items.map((r) => ({
        uid: r.uid,
        label: r.doNo,
      }));
  }
}

export interface StaffOption {
  uid: string;
  name: string;
  isActive: boolean;
}

export const fetchStaff = (): Promise<StaffOption[]> => api<StaffOption[]>('/users', { withoutBranch: true });
